// Welle U10: gemeinsame Darstellung von Datenschutzerklärung, Verarbeitungsübersicht
// und Impressum — genutzt von der öffentlichen Seite, der Registrierung, der
// Datenschutz-Seite in der App und dem Bestätigen-Fenster auf Heute.
import React, { useEffect, useState } from 'react';
import { ladeRechtliches, Rechtliches, Verarbeitung } from './rechtliches-api';

export function useRechtliches() {
  const [daten, setDaten] = useState<Rechtliches | null>(null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    let aktiv = true;
    ladeRechtliches().then(d => { if (aktiv) setDaten(d); }).catch(() => { if (aktiv) setFehler(true); });
    return () => { aktiv = false; };
  }, []);
  return { daten, fehler };
}

const absatz: React.CSSProperties = { margin: 0, fontSize: 13, lineHeight: 1.7, color: 'var(--ld-text-primary)' };
const ueberschrift: React.CSSProperties = { margin: '0 0 6px', fontSize: 14, fontWeight: 800, color: 'var(--ld-text-primary)' };

export function VerarbeitungsListe({ verarbeitungen }: { verarbeitungen: Verarbeitung[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {verarbeitungen.map((v, i) => (
        <div key={i} style={{ padding: 14, background: 'var(--ld-bg-input)', borderRadius: 'var(--ld-radius-md)', fontSize: 13 }}>
          <div style={{ fontWeight: 700, color: 'var(--ld-text-primary)', marginBottom: 6 }}>{v.zweck}</div>
          <div style={{ color: 'var(--ld-text-secondary)', lineHeight: 1.5 }}>
            {v.rechtsgrundlage && <div>Rechtsgrundlage: {v.rechtsgrundlage}</div>}
            <div>Daten: {v.daten.join(', ')}</div>
            <div>Speicherdauer: {v.speicherdauer}</div>
            <div>Empfänger: {v.empfaenger}</div>
            {v.drittland && <div style={{ color: '#B45309', fontWeight: 600 }}>Drittland: {v.drittland}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Laedt({ fehler }: { fehler: boolean }) {
  return <p style={{ ...absatz, color: 'var(--ld-text-secondary)' }}>{fehler ? 'Der Text konnte nicht geladen werden. Bitte später erneut versuchen.' : 'Lädt …'}</p>;
}

export function DatenschutzerklaerungText() {
  const { daten, fehler } = useRechtliches();
  if (!daten) return <Laedt fehler={fehler} />;
  const d = daten.datenschutzerklaerung;
  return (
    <div className="ld-rechtstext" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <p style={{ ...absatz, color: 'var(--ld-text-secondary)' }}>Stand: {d.stand}</p>
      {d.abschnitte.map(a => (
        <section key={a.titel}>
          <h3 style={ueberschrift}>{a.titel}</h3>
          {a.typ === 'verarbeitungen' && <VerarbeitungsListe verarbeitungen={d.verarbeitungen} />}
          {a.typ === 'empfaenger' && (
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {d.empfaenger.map(e => (
                <li key={e.id} style={absatz}>
                  <strong>{e.name}</strong> – {e.rolle}{e.standort ? `, ${e.standort}` : ''}{e.drittland ? `. ${e.drittland}` : ''}
                </li>
              ))}
            </ul>
          )}
          {(a.absaetze ?? []).map((t, i) => <p key={i} style={{ ...absatz, marginTop: i ? 6 : 0 }}>{t}</p>)}
        </section>
      ))}
    </div>
  );
}

export function ImpressumText() {
  const { daten, fehler } = useRechtliches();
  if (!daten) return <Laedt fehler={fehler} />;
  const i = daten.impressum;
  return (
    <div className="ld-rechtstext" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <section>
        <h3 style={ueberschrift}>Anbieter</h3>
        {i.name && <p style={absatz}>{i.name}</p>}
        {i.anschrift.map(z => <p key={z} style={absatz}>{z}</p>)}
      </section>
      <section>
        <h3 style={ueberschrift}>Kontakt</h3>
        {i.email && <p style={absatz}>E-Mail: <a href={`mailto:${i.email}`} style={{ color: 'var(--ld-accent)' }}>{i.email}</a></p>}
        {i.zweiter_kontakt && <p style={absatz}>{i.zweiter_kontakt.art}: {i.zweiter_kontakt.wert}</p>}
      </section>
      {i.ust_idnr && (
        <section>
          <h3 style={ueberschrift}>Umsatzsteuer-ID</h3>
          <p style={absatz}>{i.ust_idnr}</p>
        </section>
      )}
    </div>
  );
}

// Dezente Linkzeile „Impressum · Datenschutzerklärung“ (Login, Registrierung, Seitenleiste,
// Mehr-Menü, Einstellungen). neuerTab: z. B. in der Registrierung, damit Eingaben bleiben.
export function RechtsLinks({ neuerTab = false, ausrichtung = 'center', style }: {
  neuerTab?: boolean; ausrichtung?: 'center' | 'flex-start'; style?: React.CSSProperties;
}) {
  const ziel = neuerTab ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  const link: React.CSSProperties = { color: 'var(--ld-text-secondary)', textDecoration: 'underline', textUnderlineOffset: 2 };
  return (
    <nav aria-label="Rechtliches" style={{ display: 'flex', justifyContent: ausrichtung, gap: 8, fontSize: 12, color: 'var(--ld-text-tertiary)', ...style }}>
      <a href="/impressum" {...ziel} style={link}>Impressum</a>
      <span aria-hidden>·</span>
      <a href="/datenschutzerklaerung" {...ziel} style={link}>Datenschutzerklärung</a>
    </nav>
  );
}
